import type { CommunicationLogDto } from '../../notifications/api/communicationsApi';

/**
 * Who got which e-mail about this ZK, read from the notification log of the
 * order (`GET /notifications/log/order/:id`, the existing owner; the backend
 * decides what the reader may see). The card uses it twice:
 *
 * - „Aktywność”: one entry per sent notice (event + cycle) naming the
 *   recipients — customer, Operacje, administrator;
 * - stale hints: after the confirmation was sent the bar no longer asks to
 *   send it, and the shortage hint follows the real outcome of the notice of
 *   the current status (ma już / nie udało się wysłać / dostanie powiadomienie).
 *
 * `null` log = unknown (not loaded / no access): nothing is claimed either way.
 */
export const CONFIRMATION_NOTICE_EVENTS = ['ORDER_CONFIRMED_STOCK', 'ORDER_CONFIRMED_STOCK_CUSTOMER', 'CUSTOMER_CONFIRMATION_MANUAL'];
export const SHORTAGE_NOTICE_EVENT = 'ORDER_STATUS_SHORTAGE';
const CUSTOMER_EVENTS = new Set(['ORDER_CONFIRMED_STOCK_CUSTOMER', 'CUSTOMER_CONFIRMATION_MANUAL', 'CUSTOMER_STATUS_UPDATE_MANUAL', 'CUSTOMER_OFFER_MANUAL']);

export interface OrderNotificationEntry {
  id: string;
  at: string;
  eventType: string;
  subject: string | null;
  /** True for e-mails to the customer (the recipient is the customer's address). */
  toCustomer: boolean;
  /** Names (users) or addresses (customer), deduplicated across channels. */
  recipients: string[];
  /** Recipients the notice failed for. */
  failed: string[];
  /** The log rows of this notice (message body for readers that get it). */
  logs: CommunicationLogDto[];
}

export interface OrderNotificationSummary {
  entries: OrderNotificationEntry[];
  /** null = unknown (no log); otherwise whether a confirmation notice went out. */
  confirmationSent: boolean | null;
  confirmationRecipients: string[];
  /**
   * Outcome of the shortage notice per ZK status (backend cycle `shortage:<status>`,
   * one notice per order and status): `sent` when at least one recipient got it,
   * `failed` when every attempt failed. null = unknown (no log).
   */
  shortageNotices: Record<string, 'sent' | 'failed'> | null;
}

/** Bar wording of the shortage notice for the order's current status. */
export type ShortageNoticeState = 'sent' | 'failed' | 'pending' | null;

/**
 * The shortage notice of the current status, read from the log (not guessed
 * from the status): sent / failed, or `pending` while the background notice
 * has not written its row yet (the card refetches the log after a move).
 */
export function shortageNoticeFor(summary: OrderNotificationSummary, status: string): ShortageNoticeState {
  if (!summary.shortageNotices) return null;
  return summary.shortageNotices[status] ?? 'pending';
}

const recipientLabel = (log: CommunicationLogDto) => (log.userName || log.recipient || '').trim();

/** Logs written within a minute for one event and cycle belong to one notice. */
const groupKey = (log: CommunicationLogDto) => {
  const cycle = typeof log.metadata?.cycleKey === 'string' ? log.metadata.cycleKey : null;
  return `${log.eventType}|${cycle ?? log.createdAt.slice(0, 16)}`;
};

export function summarizeOrderNotifications(logs: CommunicationLogDto[] | null): OrderNotificationSummary {
  if (!logs) return { entries: [], confirmationSent: null, confirmationRecipients: [], shortageNotices: null };
  const groups = new Map<string, OrderNotificationEntry>();
  [...logs]
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    .forEach((log) => {
      const key = groupKey(log);
      const entry = groups.get(key) ?? {
        id: `notification-${log.id}`,
        at: log.createdAt,
        eventType: log.eventType,
        subject: log.subject,
        toCustomer: CUSTOMER_EVENTS.has(log.eventType),
        recipients: [],
        failed: [],
        logs: [],
      };
      entry.logs.push(log);
      const label = recipientLabel(log);
      if (label) {
        const target = log.status === 'failed' ? entry.failed : entry.recipients;
        if (!target.includes(label)) target.push(label);
      }
      groups.set(key, entry);
    });
  const entries = Array.from(groups.values()).filter((entry) => entry.recipients.length > 0 || entry.failed.length > 0);
  const sent = (events: string[]) => entries.filter((entry) => events.includes(entry.eventType) && entry.recipients.length > 0);
  const confirmations = sent(CONFIRMATION_NOTICE_EVENTS);
  const shortageNotices: Record<string, 'sent' | 'failed'> = {};
  entries
    .filter((entry) => entry.eventType === SHORTAGE_NOTICE_EVENT)
    .forEach((entry) => {
      const cycle = entry.logs.map((log) => log.metadata?.cycleKey).find((key) => typeof key === 'string') as string | undefined;
      if (!cycle?.startsWith('shortage:')) return;
      const status = cycle.slice('shortage:'.length);
      if (shortageNotices[status] === 'sent') return;
      shortageNotices[status] = entry.recipients.length > 0 ? 'sent' : 'failed';
    });
  return {
    entries,
    confirmationSent: confirmations.length > 0,
    confirmationRecipients: Array.from(new Set(confirmations.flatMap((entry) => entry.recipients))),
    shortageNotices,
  };
}
